<template>
  <div class="public-visitor-registration">
    <!-- Main Content -->
    <br>
    <VContainer 
      fluid 
      class="mt-10"
    >
      <VRow no-gutters>
        <VCol 
          cols="12" 
          md="8" 
          offset-md="2"
          lg="6" 
          offset-lg="3"
        >
          <div class="registration-container pa-4 pa-md-6">
            <!-- Loading State -->
            <div
              v-if="loading"
              class="text-center py-12"
            >
              <VProgressCircular
                indeterminate
                size="64"
                color="primary"
                class="mb-4"
              />
              <h4 class="text-h5 mb-2">
                Loading...
              </h4>
              <p class="text-body-1 text-medium-emphasis">
                {{ loadingMessage }}
              </p>
            </div>

            <!-- Error State -->
            <VAlert
              v-else-if="error"
              type="error"
              variant="tonal"
              class="mb-6"
              prominent
            >
              <h4 class="text-h6 mb-2">
                Registration Error
              </h4>
              <p class="mb-0">
                {{ error }}
              </p>
              <template #append>
                <VBtn
                  variant="outlined"
                  color="error"
                  size="small"
                  @click="initializeRegistration"
                >
                  Try Again
                </VBtn>
              </template>
            </VAlert>

            <!-- Registration Flow -->
            <div v-else>
              <!-- Progress Stepper -->
              <VCard class="mb-6">
                <VCardText class="pa-4">
                  <VProgressLinear
                    :model-value="(currentStep + 1) / totalSteps * 100"
                    color="primary"
                    height="8"
                    rounded
                    class="mb-4"
                  />
                  <div class="d-flex justify-space-between text-caption">
                    <span 
                      v-for="(step, index) in stepperItems" 
                      :key="index"
                      :class="index <= currentStep ? 'text-primary font-weight-bold' : 'text-medium-emphasis'"
                    >
                      {{ step.title }}
                    </span>
                  </div>
                </VCardText>
              </VCard>

              <!-- Patient Information Display -->
              <VCard
                v-if="patientInfo"
                class="mb-6"
                color="primary"
                variant="tonal"
              >
                <VCardText>
                  <div class="d-flex align-center">
                    <VAvatar
                      color="primary"
                      size="48"
                      class="me-4"
                    >
                      <VIcon icon="tabler-user-heart" />
                    </VAvatar>
                    <div>
                      <h5 class="text-h6 font-weight-bold">
                        Visiting: {{ patientInfo.name }}
                      </h5>
                      <p class="text-body-2 mb-0">
                        UHID: {{ patientInfo.uhid }} | Episode: {{ patientInfo.episode_code }}
                      </p>
                      <p class="text-body-2 mb-0 text-medium-emphasis">
                        {{ patientInfo.department }} - {{ patientInfo.location }}
                      </p>
                    </div>
                  </div>
                </VCardText>
              </VCard>

              <!-- Step 1: Consent & Terms -->
              <PublicConsentForm
                v-if="currentStep === 0"
                v-model="consentData"
                :hospital-name="hospitalName"
                :patient-name="patientInfo?.name"
                @next="handleNext"
              />

              <!-- Step 2: Visitor Information -->
              <PublicVisitorForm
                v-else-if="currentStep === 1"
                v-model="visitorData"
                :patient-info="patientInfo"
                :errors="formErrors"
                @next="handleNext"
                @back="currentStep--"
              />

              <!-- Step 3: Success Page -->
              <PublicVisitorSuccess
                v-else-if="currentStep === 2"
                :visitor="registeredVisitor"
                :patient-info="patientInfo"
                @download="downloadPass"
                @email="emailPass"
                @sms="smsPass"
                @new-registration="startNewRegistration"
              />
            </div>
          </div>
        </VCol>
      </VRow>
    </VContainer>

    <!-- Help Dialog -->
    <VDialog
      v-model="showHelpDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle class="d-flex align-center">
          <VIcon
            icon="tabler-help-circle"
            class="me-2"
          />
          Need Help?
        </VCardTitle>
        <VCardText>
          <div class="mb-4">
            <h6 class="text-subtitle-1 font-weight-bold mb-2">
              Registration Process
            </h6>
            <ol class="text-body-2">
              <li>Review and accept hospital terms and policies</li>
              <li>Fill in your personal and visit information</li>
              <li>Receive your digital visitor pass with QR code</li>
            </ol>
          </div>
          
          <div class="mb-4">
            <h6 class="text-subtitle-1 font-weight-bold mb-2">
              Need Assistance?
            </h6>
            <p class="text-body-2 mb-2">
              Contact our reception desk:
            </p>
            <ul class="text-body-2">
              <li>Phone: {{ hospitalInfo.phone }}</li>
              <li>Email: {{ hospitalInfo.email }}</li>
            </ul>
          </div>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            color="primary"
            @click="showHelpDialog = false"
          >
            Got it
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- Action Loading Dialog -->
    <VDialog
      v-model="actionLoading"
      width="300"
      persistent
    >
      <VCard>
        <VCardText class="text-center pa-6">
          <VProgressCircular
            indeterminate
            size="48"
            color="primary"
            class="mb-4"
          />
          <p class="text-body-1">
            {{ actionMessage }}
          </p>
        </VCardText>
      </VCard>
    </VDialog>

    <!-- Success Action Snackbar -->
    <VSnackbar
      v-model="actionSuccess"
      color="success"
      location="top"
    >
      {{ actionSuccessMessage }}
    </VSnackbar>
  </div>
</template>

<script setup>
import { useToast } from '@/composables/useToast'
import publicVisitorService from '@/services/care-visit/publicVisitor.service'
import { useRoute, useRouter } from 'vue-router'

// Components
import PublicConsentForm from '@/components/care-visit/public/PublicConsentForm.vue'
import PublicVisitorForm from '@/components/care-visit/public/PublicVisitorForm.vue'
import PublicVisitorSuccess from '@/components/care-visit/public/PublicVisitorSuccess.vue'

// Router and composables
const route = useRoute()
const router = useRouter()
const toast = useToast()

definePage({ 
  meta: { 
    layout: 'client', 
    public: true, 
    unauthenticatedOnly: true,
    title: 'General Visitor Registration',
  }, 
})

// Reactive data
const loading = ref(true)
const loadingMessage = ref('Validating registration link...')
const error = ref(null)
const currentStep = ref(0)
const totalSteps = 3

// Form data
const consentData = ref({
  privacyPolicy: false,
  hospitalRules: false,
  dataProcessing: false,
  photoConsent: false,
})

const visitorData = ref({
  firstName: '',
  lastName: '',
  phone: '',
  email: '',
  idType: '',
  idNumber: '',
  relationshipWithPatient: '',
  visitPurpose: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
})

const formErrors = ref({})

// Patient and registration data
const patientInfo = ref(null)
const registeredVisitor = ref(null)

// UI state
const showHelpDialog = ref(false)
const actionLoading = ref(false)
const actionMessage = ref('')
const actionSuccess = ref(false)
const actionSuccessMessage = ref('')

// Configuration
const hospitalName = ref('City General Hospital')

const hospitalInfo = ref({
  phone: '+1 (555) 123-4567',
  email: 'info@citygeneralhospital.com',
})

// Computed
const stepperItems = computed(() => [
  { title: 'Consent' },
  { title: 'Information' },
  { title: 'Complete' },
])

// Route parameters
const episodeId = computed(() => route.query.episode_id)
const customerId = computed(() => route.query.customer_id)

// Methods
const initializeRegistration = async () => {
  loading.value = true
  loadingMessage.value = 'Validating registration link...'
  error.value = null

  try {
    // Validate required parameters
    if (!episodeId.value || !customerId.value) {
      throw new Error('Invalid registration link. Missing required parameters.')
    }

    // Fetch patient information
    loadingMessage.value = 'Loading patient information...'


    const response = await publicVisitorService.validateRegistrationLink({
      episode_id: episodeId.value,
      customer_id: customerId.value,
    })

    // patientInfo.value =  {
    //   name: '',
    //   uhid: '',
    //   episode_code: '',
    //   allows_visitors: true,
    //   department: 'N/A',
    //   admission_date: '',
    //   discharge_date: '',
    // }
    if (!response?.data.allows_visitors) {
      throw new Error('This episode is not currently accepting visitor registrations.')
    }

    patientInfo.value = response.data ?? {}

    // Check if episode is still active and allows visitors

    loading.value = false
  } catch (err) {
    console.error('Registration initialization error:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load registration information'
    loading.value = false
  }
}

const handleNext = async () => {
  if (currentStep.value === 0) {
    // Validate consent
    if (!consentData.value.privacyPolicy || !consentData.value.hospitalRules || !consentData.value.dataProcessing) {
      toast.error('Please accept all required terms to continue')
      
      return
    }
    currentStep.value++
  } else if (currentStep.value === 1) {
    // Submit registration
    await submitRegistration()
  }
}

const submitRegistration = async () => {
  formErrors.value = {}
  actionLoading.value = true
  actionMessage.value = 'Registering visitor...'

  try {
    // Prepare registration data
    const registrationData = {
      // Patient/Episode information
      episode_id: episodeId.value,
      customer_id: customerId.value,
      
      // Visitor personal information
      first_name: visitorData.value.firstName,
      last_name: visitorData.value.lastName,
      name: `${visitorData.value.firstName} ${visitorData.value.lastName}`,
      phone: visitorData.value.phone,
      email: visitorData.value.email || null,
      national_id_type: visitorData.value.idType,
      national_id: visitorData.value.idNumber,
      
      // Visit information
      relationship_with_patient: visitorData.value.relationshipWithPatient,
      visit_purpose: visitorData.value.visitPurpose,
      visitor_type: 'NORMAL', // Default for public registration
      
      // Emergency contact
      emergency_contact_name: visitorData.value.emergencyContactName || null,
      emergency_contact_phone: visitorData.value.emergencyContactPhone || null,
      
      // Consent data
      data_processing_consent: consentData.value.dataProcessing,
      privacy_policy_accepted: consentData.value.privacyPolicy,
      terms_accepted: consentData.value.hospitalRules,
      photo_consent: consentData.value.photoConsent,
      
      // Additional metadata
      registration_source: 'public_link',
      patient_name: patientInfo.value.name,
      patient_uhid: patientInfo.value.uhid,
      episode_code: patientInfo.value.episode_code,
    }

    // Submit registration
    const response = await publicVisitorService.registerVisitor(registrationData)
    
    registeredVisitor.value = response.data
    currentStep.value++
    
    actionLoading.value = false
    toast.success('Visitor registered successfully!')
    
  } catch (err) {
    actionLoading.value = false
    console.error('Registration submission error:', err)
    
    // Handle validation errors
    if (err.response?.status === 422 && err.response?.data?.errors) {
      formErrors.value = err.response.data.errors
      toast.error('Please correct the form errors and try again')
    } else if (err.response?.status === 429) {
      error.value = 'Too many registration attempts. Please try again later.'
    } else {
      const errorMessage = err.response?.data?.message || err.message || 'Registration failed'

      toast.error(errorMessage)
    }
  }
}

const downloadPass = async () => {
  actionLoading.value = true
  actionMessage.value = 'Generating visitor pass...'
  
  try {
    const response = await publicVisitorService.downloadVisitorPass(registeredVisitor.value.id)
    
    // Create download link
    const blob = new Blob([response], { type: 'application/pdf' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = `visitor-pass-${registeredVisitor.value.pass_number}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    
    actionLoading.value = false
    actionSuccess.value = true
    actionSuccessMessage.value = 'Visitor pass downloaded successfully!'
    
  } catch (err) {
    actionLoading.value = false
    console.error('Download error:', err)
    toast.error('Failed to download visitor pass')
  }
}

const emailPass = async () => {
  if (!registeredVisitor.value?.email) {
    toast.error('No email address provided')
    
    return
  }
  
  actionLoading.value = true
  actionMessage.value = 'Sending email...'
  
  try {
    await publicVisitorService.emailVisitorPass(registeredVisitor.value.id)
    
    actionLoading.value = false
    actionSuccess.value = true
    actionSuccessMessage.value = `Visitor pass emailed to ${registeredVisitor.value.email}`
    
  } catch (err) {
    actionLoading.value = false
    console.error('Email error:', err)
    toast.error('Failed to email visitor pass')
  }
}

const smsPass = async () => {
  if (!registeredVisitor.value?.phone) {
    toast.error('No phone number provided')
    
    return
  }
  
  actionLoading.value = true
  actionMessage.value = 'Sending SMS...'
  
  try {
    await publicVisitorService.smsVisitorPass(registeredVisitor.value.id)
    
    actionLoading.value = false
    actionSuccess.value = true
    actionSuccessMessage.value = `Visitor pass details sent to ${registeredVisitor.value.phone}`
    
  } catch (err) {
    actionLoading.value = false
    console.error('SMS error:', err)
    toast.error('Failed to send SMS notification')
  }
}

const startNewRegistration = () => {
  // Reset form data
  currentStep.value = 0
  consentData.value = {
    privacyPolicy: false,
    hospitalRules: false,
    dataProcessing: false,
    photoConsent: false,
  }
  visitorData.value = {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    idType: '',
    idNumber: '',
    relationshipWithPatient: '',
    visitPurpose: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
  }
  formErrors.value = {}
  registeredVisitor.value = null
  
}

// Lifecycle
onMounted(() => {
  initializeRegistration()
})
</script>

<style scoped>
.public-visitor-registration {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  min-block-size: 100vh;
}

.registration-container {
  margin-block: 0;
  margin-inline: auto;
  max-inline-size: 600px;
}

.text-medium-emphasis {
  opacity: 0.68;
}

.border-b {
  border-block-end: 1px solid rgba(var(--v-border-color), 0.12);
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .registration-container {
    padding: 16px !important;
  }
  
  .v-app-bar {
    padding-inline: 8px !important;
  }
  
  .v-app-bar-title {
    font-size: 1.1rem !important;
  }
}

@media (max-width: 480px) {
  .v-app-bar-title {
    font-size: 1rem !important;
  }
  
  .registration-container {
    padding: 12px !important;
  }
}

/* Print styles */
@media print {
  .public-visitor-registration {
    background: white !important;
  }
  
  .v-app-bar {
    display: none !important;
  }
}
</style>
